import { createRoute, useNavigate } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';
import { useState, useEffect, useMemo } from 'react';
import { useCartStore, useAuthStore } from '../store';
import { CreditCard, ShieldCheck, LogIn, MapPin, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, ExternalLink } from 'lucide-react';
import { auth, db, googleProvider } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { Helmet } from 'react-helmet-async';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/checkout',
  component: Checkout,
});

interface VendorSchedule {
  pickupSchedules: Record<string, { startTime: string; endTime: string; enabled: boolean }>;
}

function InteractiveScheduler({ 
  schedules, 
  onSelect 
}: { 
  schedules: Record<string, VendorSchedule>, 
  onSelect: (time: string) => void 
}) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);

  const { availableDays, getStartEndTime } = useMemo(() => {
    const scheds = Object.values(schedules).filter(Boolean) as VendorSchedule[];
    if (scheds.length === 0) {
      return { 
        availableDays: [1,2,3,4,5], 
        getStartEndTime: () => ({ startTime: '09:00', endTime: '17:00' }) 
      };
    }

    const days = [0,1,2,3,4,5,6].filter(day => {
      return scheds.every(s => s.pickupSchedules && s.pickupSchedules[day.toString()]?.enabled);
    });

    const getStartEndTime = (day: number) => {
      const startTimes = scheds.map(s => (s.pickupSchedules && s.pickupSchedules[day.toString()]?.startTime) || '09:00');
      const endTimes = scheds.map(s => (s.pickupSchedules && s.pickupSchedules[day.toString()]?.endTime) || '17:00');
      
      const start = startTimes.sort().reverse()[0];
      const end = endTimes.sort()[0];
      return { startTime: start, endTime: end };
    };
    
    return { availableDays: days, getStartEndTime };
  }, [schedules]);

  const dates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setDate(today.getDate() + (weekOffset * 7));
    
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [weekOffset]);

  const timeSlots = useMemo(() => {
    if (!selectedDate) return [];
    const day = selectedDate.getDay();
    const { startTime, endTime } = getStartEndTime(day);
    
    const slots: string[] = [];
    let current = new Date(selectedDate);
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    
    current.setHours(startH, startM, 0, 0);
    const end = new Date(selectedDate);
    end.setHours(endH, endM, 0, 0);

    // If selected date is today, only show future slots (plus 30 min buffer)
    const now = new Date();
    now.setMinutes(now.getMinutes() + 30);

    while (current < end) {
      if (current > now) {
        slots.push(current.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      }
      current.setMinutes(current.getMinutes() + 30);
    }
    return slots;
  }, [selectedDate, getStartEndTime]);

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    if (selectedDate) {
      const formattedDate = selectedDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
      onSelect(`${formattedDate} at ${time}`);
    }
  };

  if (availableDays.length === 0) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl text-sm border border-red-100 dark:border-red-900/50">
        The items in your cart are from vendors with conflicting pickup schedules. Please checkout separately.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <button 
          type="button"
          onClick={() => setWeekOffset(prev => Math.max(0, prev - 1))}
          disabled={weekOffset === 0}
          className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-30 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
        </button>
        <span className="text-sm font-medium text-neutral-900 dark:text-white">
          {dates[0].toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </span>
        <button 
          type="button"
          onClick={() => setWeekOffset(prev => prev + 1)}
          className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {dates.map((date, i) => {
          const isAvailable = availableDays.includes(date.getDay());
          const isSelected = selectedDate?.toDateString() === date.toDateString();
          const isPast = date < new Date(new Date().setHours(0,0,0,0));
          const disabled = !isAvailable || isPast;

          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => {
                setSelectedDate(date);
                setSelectedTime(null);
                onSelect('');
              }}
              className={`flex flex-col items-center p-2 rounded-xl border transition-all ${
                isSelected 
                  ? 'bg-neutral-900 dark:bg-white border-neutral-900 dark:border-white text-white dark:text-neutral-900 shadow-sm' 
                  : disabled
                    ? 'opacity-30 cursor-not-allowed border-transparent'
                    : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 hover:border-neutral-400 dark:hover:border-neutral-700 text-neutral-900 dark:text-white'
              }`}
            >
              <span className="text-[10px] uppercase font-medium mb-1 opacity-80">
                {date.toLocaleDateString(undefined, { weekday: 'short' })}
              </span>
              <span className="text-sm font-semibold">
                {date.getDate()}
              </span>
            </button>
          );
        })}
      </div>

      {selectedDate && (
        <div className="pt-4 border-t border-neutral-100 dark:border-neutral-800 mt-4">
          <h4 className="text-sm font-medium text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
            Available Times
          </h4>
          {timeSlots.length > 0 ? (
            <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-2 pb-2 custom-scrollbar">
              {timeSlots.map(time => (
                <button
                  key={time}
                  type="button"
                  onClick={() => handleTimeSelect(time)}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all border ${
                    selectedTime === time
                      ? 'bg-neutral-900 dark:bg-white border-neutral-900 dark:border-white text-white dark:text-neutral-900 shadow-sm'
                      : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 hover:border-neutral-400 dark:hover:border-neutral-700 text-neutral-700 dark:text-neutral-300'
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 italic">No available times for this date.</p>
          )}
        </div>
      )}
    </div>
  );
}

function Checkout() {
  const { items, total, clearCart } = useCartStore();
  const { user, fetchProfile } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [pickupTimes, setPickupTimes] = useState<Record<string, string>>({});
  const [vendorLocations, setVendorLocations] = useState<Record<string, string>>({});
  const [vendorSchedules, setVendorSchedules] = useState<Record<string, VendorSchedule>>({});
  const [vendorNames, setVendorNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchVendorLocations = async () => {
      const uniqueVendorIds = Array.from(new Set(items.map(item => item.vendorId).filter(Boolean))) as string[];
      const locations: Record<string, string> = {};
      const schedules: Record<string, VendorSchedule> = {};
      const names: Record<string, string> = {};
      
      for (const vId of uniqueVendorIds) {
        try {
          const vDoc = await getDoc(doc(db, 'stores', vId));
          if (vDoc.exists()) {
            const data = vDoc.data();
            if (data.storeName) {
              names[vId] = data.storeName;
            }
            if (data.pickupLocation) {
              locations[vId] = data.pickupLocation;
            }
            if (data.pickupSchedules) {
              schedules[vId] = {
                pickupSchedules: data.pickupSchedules
              };
            } else if (data.pickupDays && data.pickupStartTime && data.pickupEndTime) {
              // Migrate old format
              const newSchedules: Record<string, { startTime: string; endTime: string; enabled: boolean }> = {};
              for (let i = 0; i < 7; i++) {
                newSchedules[i.toString()] = {
                  enabled: data.pickupDays.includes(i),
                  startTime: data.pickupStartTime,
                  endTime: data.pickupEndTime
                };
              }
              schedules[vId] = {
                pickupSchedules: newSchedules
              };
            }
          }
        } catch (err) {
          console.error(`Failed to fetch location for vendor ${vId}`, err);
        }
      }
      setVendorLocations(locations);
      setVendorSchedules(schedules);
      setVendorNames(names);
    };

    if (items.length > 0) {
      fetchVendorLocations();
    }
  }, [items]);

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      const user = userCredential.user;
      
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      let isAdmin = false;
      let role = 'user';

      if (userSnap.exists()) {
        const data = userSnap.data();
        isAdmin = data.isAdmin || false;
        role = data.role || 'user';
      }

      await setDoc(
        userRef,
        {
          email: user.email,
          lastLogin: serverTimestamp(),
        },
        { merge: true }
      );

      await fetchProfile(user.uid);
    } catch (err: any) {
      setError(err.message || 'Failed to log in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (provider: 'stripe') => {
    const emailToUse = user ? user.email : guestEmail;

    if (!user && !guestEmail) {
      setError('Please enter your email address to continue.');
      return;
    }

    if (!user && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let token: string | undefined = undefined;
      if (user) {
        token = await auth.currentUser?.getIdToken();
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const uniqueVendorIds = Array.from(new Set(items.map(item => item.vendorId).filter(Boolean))) as string[];
      const missingPickupTimes = uniqueVendorIds.filter(vId => !pickupTimes[vId]);
      if (missingPickupTimes.length > 0) {
        throw new Error('Please select a pickup time for all vendors.');
      }

      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          provider,
          email: emailToUse,
          pickupTimes,
          items: items.map((item) => ({ 
            id: item.id, 
            quantity: item.quantity, 
            vendorId: item.vendorId,
            vendorName: item.vendorName
          })),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Checkout failed');
      }

      if (provider === 'stripe' && data.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (items.length === 0) {
    navigate({ to: '/cart' });
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Secure Checkout - Cardston Market</title>
        <meta name="description" content="Complete your purchase securely on Cardston Market. Pay with credit card or guest checkout." />
        <link rel="canonical" href="https://www.cardstonmarket.com/checkout" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    <div className="max-w-6xl mx-auto w-full py-12 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center gap-3 mb-12">
        <ShieldCheck className="w-8 h-8 text-neutral-900 dark:text-white stroke-[1.5]" />
        <h1 className="text-3xl font-serif font-medium text-neutral-900 dark:text-white tracking-tight">Secure Checkout</h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">
        {/* Left Pane: Order Summary */}
        <div className="flex-1">
          <h2 className="text-xl font-medium text-neutral-900 dark:text-white mb-6 font-serif">Order Summary</h2>
          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
            <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {items.map((item) => (
                <li key={item.id} className="p-6 flex items-start gap-6">
                  <div className="w-20 h-24 shrink-0 bg-neutral-100 dark:bg-neutral-800 rounded-lg overflow-hidden">
                    <img
                      src={item.imageUrl || 'https://picsum.photos/seed/product/200/300'}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-between py-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-base font-medium text-neutral-900 dark:text-white">{item.name}</h3>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Qty: {item.quantity}</p>
                        {item.vendorId && vendorLocations[item.vendorId] && (
                          <div className="flex items-start gap-1.5 mt-2 text-xs text-neutral-600 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800/50 p-2 rounded-md border border-neutral-100 dark:border-neutral-800/50">
                            <MapPin className="w-3.5 h-3.5 text-neutral-400 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-neutral-700 dark:text-neutral-300">Pickup Location:</span><br/>
                              <span className="truncate block">{vendorLocations[item.vendorId]}</span>
                              <a 
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendorLocations[item.vendorId])}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-neutral-400 hover:text-neutral-900 dark:hover:text-white mt-1 transition-colors"
                              >
                                <span>View on Maps</span>
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                      <p className="text-base font-medium text-neutral-900 dark:text-white">
                        ${((item.price * item.quantity) / 100).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="bg-neutral-50 dark:bg-neutral-800/50 p-6 border-t border-neutral-200 dark:border-neutral-800">
              <div className="flex justify-between items-center mb-2 text-sm text-neutral-500 dark:text-neutral-400">
                <span>Subtotal</span>
                <span>${(total / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center mb-6 text-sm text-neutral-500 dark:text-neutral-400">
                <span>Shipping</span>
                <span>Calculated at next step</span>
              </div>
              <div className="flex justify-between items-center pt-6 border-t border-neutral-200 dark:border-neutral-800">
                <span className="text-base font-medium text-neutral-900 dark:text-white">Total</span>
                <span className="text-2xl font-serif font-medium text-neutral-900 dark:text-white">
                  ${(total / 100).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane: Payment */}
        <div className="w-full lg:w-[400px] shrink-0">
          <h2 className="text-xl font-medium text-neutral-900 dark:text-white mb-6 font-serif">
            Payment Method
          </h2>
          
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 p-4 rounded-xl">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            <div className="bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
              <h3 className="text-sm font-medium text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
                Schedule Pickup
              </h3>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
                Select a date and time to pick up your items.
              </p>
              
              <div className="space-y-6">
                {Object.keys(vendorSchedules).map(vendorId => (
                  <div key={vendorId} className="pt-4 border-t border-neutral-100 dark:border-neutral-800 first:pt-0 first:border-0">
                    <h4 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-3">
                      {vendorNames[vendorId] || 'Vendor'}
                    </h4>
                    <InteractiveScheduler 
                      schedules={{ [vendorId]: vendorSchedules[vendorId] }} 
                      onSelect={(time) => setPickupTimes(prev => ({ ...prev, [vendorId]: time }))} 
                    />
                    {pickupTimes[vendorId] && (
                      <div className="mt-3 p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-100 dark:border-neutral-800 text-sm text-neutral-700 dark:text-neutral-300 flex items-center justify-between">
                        <span>Selected:</span>
                        <span className="font-medium text-neutral-900 dark:text-white">{pickupTimes[vendorId]}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {!user ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="guest-email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    id="guest-email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="Enter your email for the receipt"
                    className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white transition-colors text-sm bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500"
                  />
                </div>
                
                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-200 dark:border-neutral-800"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[#fafafa] dark:bg-[#0a0a0a] text-neutral-500 dark:text-neutral-400">Or sign in for faster checkout</span>
                  </div>
                </div>

                <button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-neutral-200 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-900 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign in with Google
                </button>
              </div>
            ) : (
              <div className="p-4 bg-neutral-50 dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-neutral-200 dark:bg-neutral-800 flex items-center justify-center overflow-hidden">
                    {user.photoURL ? (
                      <img src={user.photoURL} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <LogIn className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-white">{user.displayName || 'User'}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{user.email}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4 pt-4 border-t border-neutral-200 dark:border-neutral-800">
              <button
                onClick={() => handleCheckout('stripe')}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 px-6 py-4 rounded-xl text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors disabled:opacity-50 shadow-sm"
              >
                <CreditCard className="w-5 h-5" />
                Pay with Credit Card
              </button>
            </div>
          </div>
          
          <p className="mt-8 text-xs text-neutral-400 dark:text-neutral-500 text-center flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-4 h-4" />
            Payments are secure and encrypted
          </p>
        </div>
      </div>
    </div>
    </>
  );
}
